import {
  ButtonPrimeReact,
  DropdownPrimeReact,
  PrimeReactModal,
} from "@finanedi.es/storybook"
import { CreateActivoRequestBodyProps, createActivo } from "api/Activo.service"
import {
  useCuentaContableEmpresaActivo,
  useCuentaContableEmpresaAll,
} from "api/CuentaContableEmpresa.hook"
import { TIPO_INMOVILIZADO } from "api/Types/TipoInmobilizado"
import DropdownContactos from "common/components/ContactsDropdown/ContactsDropdown"
import { DropdownCuentasContablesConModal } from "common/components/DropdownsCuentasContables/DropdownCuentasContablesConModal/DropdownCuentasContablesConModal"
import { MODAL_STYLES } from "constants/primereactStyles"
import { ErrorMessage, Formik } from "formik"
import { Calendar } from "primereact/calendar"
import { InputNumber } from "primereact/inputnumber"
import { InputText } from "primereact/inputtext"
import { useTranslation } from "react-i18next"
import { backendDateFormat } from "utils/formatDate"
import * as Yup from "yup"
import { tipoInmovilizado } from "../constants/tipoInmobilizado"

export const ModalCrearActivo = ({
  isOpen = false,
  onCancel,
  onSuccess,
}: {
  isOpen: boolean
  onCancel: () => void
  onSuccess: () => void
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  const tipoInmovilizadoOptions = [
    {
      label: tipoInmovilizado(t)[TIPO_INMOVILIZADO.MATERIAL],
      value: TIPO_INMOVILIZADO.MATERIAL,
    },
    {
      label: tipoInmovilizado(t)[TIPO_INMOVILIZADO.INTANGIBLE],
      value: TIPO_INMOVILIZADO.INTANGIBLE,
    },
  ]

  const { cuentasContablesEmpresas: cuentasContablesActivo } =
    useCuentaContableEmpresaActivo()

  const { cuentasContablesEmpresas: cuentasContablesCuentasBancarias } =
    useCuentaContableEmpresaAll()

  const crearActivoSchema = Yup.object().shape({
    nombre: Yup.string()
      .nullable()
      .required(`${t("formik.msg.requiredName")}`),
    empresaId: Yup.string()
      .uuid(`${t("formik.msg.invalidContact")}`)
      .nullable(),
    fechaAdquisicion: Yup.date()
      .nullable()
      .required(`${t("errors.dateRequired")}`),
    tipoInmovilizado: Yup.number()
      .nullable()
      .required(`${t("errors.typeRequired")}`),
    importe: Yup.number()
      .nullable()
      .required(`${t("errors.taxeReq")}`),
    importeValorResidual: Yup.number().nullable(),
    importePendiente: Yup.number().nullable(),
    cuentaContableId: Yup.string()
      .nullable()
      .required(`${t("formik.msg.requiredAccount")}`),
    cuentaContableContrapartidaId: Yup.string()
      .nullable()
      .required(`${t("formik.msg.requiredAccount")}`),
    esAmortizable: Yup.boolean().nullable(),
  })

  return (
    <Formik
      validationSchema={crearActivoSchema}
      initialValues={{
        nombre: undefined,
        empresaId: undefined,
        fechaAdquisicion: new Date(),
        tipoInmovilizado: TIPO_INMOVILIZADO.MATERIAL,
        importe: undefined,
        importeValorResidual: undefined,
        importePendiente: undefined,
        cuentaContableId: undefined,
        cuentaContableContrapartidaId: undefined,
        esAmortizable: true,
      }}
      onSubmit={(values, { resetForm }) => {
        try {
          const requestBody: CreateActivoRequestBodyProps = {
            ...values,
            fechaAdquisicion: backendDateFormat(values.fechaAdquisicion),
          }
          createActivo(requestBody).then(() => {
            resetForm()
            onSuccess()
          })
        } catch (error) {
          console.error(error) // eslint-disable-line no-console
        }
      }}
    >
      {({
        values,
        errors,
        touched,
        setFieldValue,
        handleChange,
        handleSubmit,
        handleReset,
      }) => (
        <PrimeReactModal
          visible={isOpen}
          className='tw-w-128 tw-rounded-lg tw-py-5 tw-px-6 tw-bg-white'
          baseZIndex={20}
          ptContext={MODAL_STYLES}
          closable={false}
          keepInViewport={false}
          header={t("nsAssets.modalCreate.title")}
          footer={
            <div
              key='Continuar'
              className='tw-pb-1 tw-flex tw-flex-col sm:tw-flex-row tw-justify-end'
            >
              <ButtonPrimeReact
                type='reset'
                label={`${t("nsAssets.modalCreate.cancel")}`}
                className='tw-w-full md:tw-w-fit tw-h-12 !tw-bg-simple-10 !tw-text-primary hover:!tw-text-primary-over !tw-border-none tw-items-center'
                onClick={(e) => {
                  handleReset(e)
                  onCancel()
                }}
                form='createActivo'
              />
              <ButtonPrimeReact
                type='submit'
                label={`${t("nsAssets.modalCreate.create")}`}
                className='tw-w-full md:tw-w-fit tw-h-12 !tw-bg-secondary hover:!tw-bg-secondary-over !tw-text-simple-10 !tw-border-none tw-items-center'
                onClick={() => handleSubmit()}
              />
            </div>
          }
        >
          <form
            className='tw-w-full tw-flex tw-flex-col tw-text-base tw-font-normal tw-gap-4'
            name='createActivo'
          >
            <div className='tw-flex tw-flex-col tw-gap-2'>
              <label
                htmlFor='1'
                className='!tw-mb-0'
              >
                {t("nsAssets.modalCreate.name")}
              </label>
              <InputText
                name='nombre'
                type='text'
                id='1'
                className={`tw-border tw-rounded tw-h-10 tw-w-full tw-px-2 tw-py-1 ${
                  errors.nombre && touched.nombre
                    ? "!tw-border-danger"
                    : "tw-border-neutral-25"
                }`}
                onChange={handleChange}
                value={values?.nombre ?? ""}
              />
              <ErrorMessage
                name='nombre'
                className='tw-text-danger tw-text-left'
                component='div'
              />
            </div>
            <div className='tw-gap-2 tw-grid tw-grid-cols-2'>
              <label
                htmlFor='2'
                className='!tw-mb-0 tw-flex-1'
              >
                {t("nsAssets.modalCreate.contact")}
                <DropdownContactos
                  value={values.empresaId}
                  onchange={({ value }: { value: string }) =>
                    setFieldValue("empresaId", value)
                  }
                />
                <ErrorMessage
                  name='empresaId'
                  className='tw-text-danger tw-text-left'
                  component='div'
                />
              </label>
              <label
                htmlFor='3'
                className='!tw-mb-0 tw-flex-1'
              >
                {`${t("nsAssets.modalCreate.acquisitionDate")}`}
                <Calendar
                  name='fechaAdquisicion'
                  inputId='3'
                  inputClassName={`!tw-rounded tw-placeholder-neutral-50 ${
                    errors.fechaAdquisicion && touched.fechaAdquisicion
                      ? "!tw-border-danger"
                      : "tw-border-neutral-25"
                  }`}
                  clearButtonClassName='!tw-hidden'
                  onChange={handleChange}
                  value={values?.fechaAdquisicion}
                  locale={language}
                  placeholder='dd/mm/aaaa'
                  dateFormat='dd/mm/yy'
                  mask='99/99/9999'
                  className='tw-w-full tw-rounded tw-h-10'
                  showIcon
                />
                <ErrorMessage
                  name='fechaAdquisicion'
                  className='tw-text-danger tw-text-left'
                  component='div'
                />
              </label>
            </div>
            <div className='tw-flex tw-flex-col tw-gap-2'>
              <label
                htmlFor='4'
                className='!tw-mb-0'
              >
                {`${t("nsAssets.modalCreate.assetType")}`}
              </label>
              <DropdownPrimeReact
                id='4'
                name='tipoInmovilizado'
                className={`${
                  errors.tipoInmovilizado && touched.tipoInmovilizado
                    ? "!tw-border-danger"
                    : "!tw-border-neutral-25"
                }`}
                placeholder={`${t(
                  "nsAssets.modalCreate.assetTypePlaceholder"
                )}`}
                onChange={handleChange}
                value={values?.tipoInmovilizado}
                options={tipoInmovilizadoOptions}
              />
              <ErrorMessage
                name='tipoInmovilizado'
                className='tw-text-danger tw-text-left'
                component='div'
              />
            </div>
            <div className='tw-flex tw-flex-col tw-gap-2'>
              <label
                htmlFor='5'
                className='!tw-mb-0'
              >
                {`${t("nsAssets.modalCreate.amount")}`}
              </label>
              <InputNumber
                inputId='5'
                name='importe'
                inputClassName={`tw-w-fulll tw-text-right tw-rounded tw-h-10 [&>:nth-child(3)]:!tw-font-roboto [&>.p-placeholder]:!tw-text-neutral-50 ${
                  errors.importe && touched.importe
                    ? "!tw-border-danger"
                    : "!tw-border-neutral-25"
                }`}
                className='tw-w-full'
                mode='currency'
                currency='EUR'
                onChange={(e) => {
                  setFieldValue("importe", e.value)
                }}
                value={values?.importe}
                maxFractionDigits={2}
                minFractionDigits={2}
                min={0}
              />
              <ErrorMessage
                name='importe'
                className='tw-text-danger tw-text-left'
                component='div'
              />
            </div>
            <div className='tw-flex tw-flex-row tw-gap-2'>
              <label
                htmlFor='6'
                className='!tw-mb-0 tw-w-1/2'
              >
                {`${t("nsAssets.modalCreate.initialResidualValue")}`}
                <InputNumber
                  inputId='6'
                  name='importeValorResidual'
                  className='tw-w-full'
                  inputClassName={`${
                    errors.importeValorResidual && touched.importeValorResidual
                      ? "!tw-border-danger"
                      : "!tw-border-neutral-25"
                  }`}
                  mode='currency'
                  currency='EUR'
                  onChange={(e) => {
                    setFieldValue("importeValorResidual", e.value)
                  }}
                  value={values?.importeValorResidual}
                  maxFractionDigits={2}
                  minFractionDigits={2}
                  min={0}
                />
                <ErrorMessage
                  name='initialResidualValue'
                  className='tw-text-danger tw-text-left'
                  component='div'
                />
              </label>
              <label
                htmlFor='7'
                className='!tw-mb-0 tw-w-1/2'
              >
                {`${t("nsAssets.modalCreate.pending")}`}
                <InputNumber
                  inputId='7'
                  name='importePendiente'
                  className='tw-w-full'
                  inputClassName={`${
                    errors.importePendiente && touched.importePendiente
                      ? "!tw-border-danger"
                      : "!tw-border-neutral-25"
                  }`}
                  mode='currency'
                  currency='EUR'
                  onChange={(e) => {
                    setFieldValue("importePendiente", e.value)
                  }}
                  value={values?.importePendiente}
                  maxFractionDigits={2}
                  minFractionDigits={2}
                  min={0}
                />
                <ErrorMessage
                  name='initialResidualValue'
                  className='tw-text-danger tw-text-left'
                  component='div'
                />
              </label>
            </div>
            <div className='tw-flex tw-flex-col tw-gap-2'>
              <label
                htmlFor='8'
                className='!tw-mb-0'
              >
                {`${t("nsAssets.modalCreate.assetAccountingAccount")}`}
              </label>
              <DropdownCuentasContablesConModal
                cuentasContablesOptions={cuentasContablesActivo}
                id='8'
                inputIdName='cuentaContableId'
                dropdownValue={values.cuentaContableId}
                onChange={handleChange}
                errors={errors}
                touched={touched}
              />
              <ErrorMessage
                name='cuentaContableId'
                className='tw-text-danger tw-text-left'
                component='div'
              />
            </div>
            <div className='tw-flex tw-flex-col tw-gap-2'>
              <label
                htmlFor='9'
                className='!tw-mb-0'
              >
                {`${t("nsAssets.modalCreate.accountingAccountCounterpart")}`}
              </label>
              <DropdownCuentasContablesConModal
                cuentasContablesOptions={cuentasContablesCuentasBancarias}
                id='9'
                inputIdName='cuentaContableContrapartidaId'
                dropdownValue={values.cuentaContableContrapartidaId}
                onChange={handleChange}
                errors={errors}
                touched={touched}
              />
              <ErrorMessage
                name='cuentaContableContrapartidaId'
                className='tw-text-danger tw-text-left'
                component='div'
              />
            </div>
            <div className='tw-flex'>
              <label
                htmlFor='esAmortizable'
                className='tw-grid tw-select-none tw-grid-flow-col tw-items-center tw-gap-3 tw-cursor-pointer tw-mb-0'
              >
                <input
                  name='esAmortizable'
                  onChange={(e) => {
                    setFieldValue("esAmortizable", !e.target.checked)
                  }}
                  checked={!values.esAmortizable}
                  type='checkbox'
                  id='esAmortizable'
                  className='tw-peer tw-col-start-1 tw-row-start-1 tw-h-5 tw-w-5 tw-appearance-none tw-rounded tw-border tw-border-neutral-25 tw-ring-transparent checked:tw-border-primary checked:tw-bg-primary tw-cursor-pointer'
                />
                <svg
                  viewBox='0 0 14 14'
                  fill='none'
                  className='tw-invisible tw-col-start-1 tw-row-start-1 tw-stroke-white peer-checked:tw-visible'
                >
                  <path
                    d='M3 8L6 11L11 3.5'
                    strokeWidth='1'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    data-testid='svg'
                  />
                </svg>
                {`${t("nsAssets.modalCreate.notAmortizable")}`}
              </label>
            </div>
          </form>
        </PrimeReactModal>
      )}
    </Formik>
  )
}
